import { CacheControl } from '@tusbar/cache-control'
import Router from 'next/router'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { CollectionFilterQueryParamsContextProvider } from '../../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { usePagesDirectoryStableQueryParams } from '../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { FETCH_SEARCH_REDIRECTS } from '../../../../lib/graphql/queries'
import { cachePolicies } from '../../../../lib/response-cache'
import { singleQueryParamValue } from '../../../../lib/utils'
import { RedirectSet } from '../../../../types/graphql/server-fixtures'
import { MagnoliaPage } from '../../MagnoliaApp'

import { SearchPageView } from './SearchPageView'

export const SEARCH_REGEX =
  /^[a-zA-Z0-9 ™\!\@\#\$\%\^\&\*\(\)\_\+\-\=\`\~\\\/\?\<\>\,\.\;\:\'\"\|]*$/

export const SearchPage: MagnoliaPage<{
  error: Error | undefined
  ssrQueryString: string
  statusCode: number | undefined
}> = ({ error, ssrQueryString, statusCode }) => {
  const { query: stableQueryParams } = usePagesDirectoryStableQueryParams()
  const computedQuery = singleQueryParamValue(stableQueryParams.q, ssrQueryString)
  return (
    <CollectionFilterQueryParamsContextProvider
      collectionHandle='search'
      useQueryParamsImpl={usePagesDirectoryStableQueryParams}
    >
      <SearchPageView query={computedQuery} statusCode={statusCode} error={error} />
    </CollectionFilterQueryParamsContextProvider>
  )
}

SearchPage.getInitialProps = async function (context) {
  const client = context.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const query = singleQueryParamValue(context.query.q, '')
  let error: Error | undefined = undefined

  const isValidSearchQuery = SEARCH_REGEX.exec(query) !== null

  if (!isValidSearchQuery && context.res) {
    context.res.statusCode = 410
  }

  if (query && query.trim()) {
    try {
      // @TODO: remove `any` generics for full type-safety
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await client.query<any, any, any>({
        query: FETCH_SEARCH_REDIRECTS,
        variables: {
          handle: 'default',
        },
      })

      if (data?.siteFixtures?.searchRedirects) {
        const searchRedirects: RedirectSet = data.siteFixtures.searchRedirects
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const statusCode = searchRedirects?.code ?? 307
        const redirects = JSON.parse(searchRedirects.redirects)
        const searchTerm = query.toLowerCase()

        if (redirects[searchTerm]) {
          if (context.res) {
            context.res.writeHead(statusCode, { Location: redirects[searchTerm] })
            context.res.end()
          } else {
            Router.replace(redirects[searchTerm])
          }
        }
      }
    } catch (err) {
      error = err as Error
    }
  }

  return {
    error,
    ssrQueryString: query,
    responseCacheControl: cachePolicy(error),
    statusCode: context.res?.statusCode,
  }
}

function cachePolicy(error: Error | undefined): CacheControl {
  return error ? cachePolicies.noCache : cachePolicies.volatileSharedHtmlPolicy
}
